import { dataFetchingClient } from '../../utils/DataFetchingClient';
import { registerMutation, useMutation, unwrapHubHttpError } from 'academy-common-lib/utils/DataFetchingClient';
import { BROCHURE_URL_BY_LANGUAGE } from '../../constants/AchievementConstants';
import { useCallback, useMemo } from 'react';
import produce from 'immer';
import { create } from 'zustand';
import { updateCache } from '../../utils/DataFetchingClientCache.new';
import { useGetByIdQuery } from '../../../__generated__/chirp/com/hubspot/academy/catalog/rpc/noauth/TrackPublicChirpService.hooks';
import { useCreateAchievementMutation } from '../../../__generated__/chirp/com/hubspot/learning/center/user/progress/rpc/AchievementChirpService.hooks';
import { updateAchievementVisibilityRpc } from '../../../__generated__/chirp/com/hubspot/learning/center/user/progress/rpc/AchievementChirpService';
import { useGetPublicAchievementV2Query } from '../../../__generated__/chirp/com/hubspot/credentials/rpc/AchievementPublicRpc.hooks';
import { ACADEMY_SUPPORTED_LANGUAGES } from 'academy-common-lib/constants/academySupportedLanguages';
import { rpcClient } from '../../client';
import UsageTracker from '../../utils/UsageTracker';
import { readUnhandledEnumCase } from 'rpc-client-utils';
import { useGetAllAchievementsForUserQuery } from '../../../__generated__/chirp/com/hubspot/credentials/rpc/AchievementRpc.hooks';
import { GET_ALL_ACHIEVEMENTS_FOR_USER_QUERY } from '../../../__generated__/chirp/com/hubspot/credentials/rpc/AchievementRpc.dfc.new';
export { GET_ALL_ACHIEVEMENTS_FOR_USER_QUERY };
export const UPDATE_ACHIEVEMENT = registerMutation({
  fieldName: 'updateAchievement',
  args: ['achievement', 'visibility'],
  fetcher({
    achievement,
    visibility
  }) {
    return rpcClient.call(updateAchievementVisibilityRpc, {
      request: {
        achievementPublicId: achievement.publicId,
        visibilityType: visibility
      }
    });
  }
});
export const usePublicAchievement = publicId => {
  var _data$achievement, _data$achievement2;
  const {
    data,
    error,
    loading
  } = useGetPublicAchievementV2Query({
    variables: {
      publicId: publicId
    },
    skip: !publicId,
    fetchPolicy: 'cache-first'
  });
  return {
    publicAchievement: data === null || data === void 0 || (_data$achievement = data.achievement) === null || _data$achievement === void 0 ? void 0 : _data$achievement.achievement,
    publicAchievementUser: data === null || data === void 0 || (_data$achievement2 = data.achievement) === null || _data$achievement2 === void 0 ? void 0 : _data$achievement2.user,
    publicAchievementError: error,
    publicAchievementErrorMessage: data === null || data === void 0 ? void 0 : data.errorMessage,
    publicAchievementLoading: loading
  };
};
export const useAllAchievements = () => {
  const {
    data,
    error: allAchievementsError,
    loading: allAchievementsLoading
  } = useGetAllAchievementsForUserQuery();
  return {
    allAchievements: data === null || data === void 0 ? void 0 : data.achievements,
    allAchievementsError: unwrapHubHttpError(allAchievementsError),
    allAchievementsLoading
  };
};
export const getBrochureRoot = language => BROCHURE_URL_BY_LANGUAGE[language] || BROCHURE_URL_BY_LANGUAGE.en;
export const useAchievementByTrackId = trackId => {
  const {
    allAchievements,
    allAchievementsLoading,
    allAchievementsError
  } = useAllAchievements();
  const foundAchievement = useMemo(() => {
    return (allAchievements || []).find(achievement => achievement.trackCredentialMappings.some(m => m.trackId === trackId));
  }, [allAchievements, trackId]);
  return {
    achievement: foundAchievement,
    achievementsLoading: allAchievementsLoading,
    achievementsError: allAchievementsError
  };
};
const getTrackInfo = (trackInfoByLanguage, language) => {
  const validLanguage = ACADEMY_SUPPORTED_LANGUAGES.find(supported => supported === language);
  if (!validLanguage) return undefined;
  return trackInfoByLanguage[validLanguage];
};
export const useAchievementTrackId = publicId => {
  const {
    publicAchievement
  } = usePublicAchievement(publicId);
  if (!publicAchievement) return undefined;
  const trackInfo = getTrackInfo(publicAchievement.courseInfoByLanguage, publicAchievement.language);
  return trackInfo === null || trackInfo === void 0 ? void 0 : trackInfo.courseId;
};
export const useAchievementLessons = publicId => {
  var _trackInfo$lessons;
  const {
    publicAchievement
  } = usePublicAchievement(publicId);
  if (!publicAchievement) return null;
  const trackInfo = getTrackInfo(publicAchievement.courseInfoByLanguage, publicAchievement.language);
  return (_trackInfo$lessons = trackInfo === null || trackInfo === void 0 ? void 0 : trackInfo.lessons) !== null && _trackInfo$lessons !== void 0 ? _trackInfo$lessons : null;
};
export const useAchievementUser = publicId => {
  const {
    publicAchievement,
    publicAchievementUser
  } = usePublicAchievement(publicId);
  if (!publicAchievement || !publicAchievementUser) return null;
  return Object.assign({
    userId: publicAchievement.userId
  }, publicAchievementUser);
};
export const useAchievementCertificateUrl = publicId => {
  var _publicAchievement$ce;
  const {
    publicAchievement
  } = usePublicAchievement(publicId);
  if (!publicAchievement) return null;
  return (_publicAchievement$ce = publicAchievement.certificate) !== null && _publicAchievement$ce !== void 0 ? _publicAchievement$ce : null;
};
export const useCreateAchievement = achievementData => {
  const [mutate, {
    data: createAchievementData,
    loading: createAchievementLoading,
    error: createAchievementError,
    called: createAchievementCalled
  }] = useCreateAchievementMutation({
    variables: achievementData,
    onCompleted: ({
      result
    }) => {
      UsageTracker.trackStandalone('Learning Center Interaction', {
        action: 'create-achievement',
        publicAchievementId: result.achievement.publicId,
        privacy: readUnhandledEnumCase(result.achievement.visibilityType)
      });
    },
    refetchQueries: [{
      query: GET_ALL_ACHIEVEMENTS_FOR_USER_QUERY
    }]
  });
  return [mutate, {
    createAchievementData,
    createAchievementLoading,
    createAchievementError,
    createAchievementCalled
  }];
};
const updateAchievementVisibilityInCache = (publicId, visibility) => {
  updateCache(GET_ALL_ACHIEVEMENTS_FOR_USER_QUERY, draft => {
    var _draft$achievements;
    const match = (_draft$achievements = draft.achievements) === null || _draft$achievements === void 0 ? void 0 : _draft$achievements.find(a => a.publicId === publicId);
    if (match) {
      match.visibility = visibility;
    }
  }, {});
};
export const updateAchievement = (achievement, visibility) => {
  return dataFetchingClient.mutate({
    mutation: UPDATE_ACHIEVEMENT,
    variables: {
      achievement,
      visibility
    },
    update() {
      updateAchievementVisibilityInCache(achievement.publicId, visibility);
    }
  });
};
const useAchievementUpdating = create(set => ({
  isUpdatingAchievement: {},
  setIsUpdatingAchievement: (publicId, isUpdatingAchievement) => set(produce(state => {
    state.isUpdatingAchievement[publicId] = isUpdatingAchievement;
  }))
}));
export const useUpdateAchievement = (achievement, visibility) => {
  const [mutate, {
    data: updateAchievementData,
    loading: updateAchievementLoading,
    error: updateAchievementError,
    called: updateAchievementCalled
  }] = useMutation(UPDATE_ACHIEVEMENT, {
    variables: {
      achievement: achievement,
      visibility: visibility !== null && visibility !== void 0 ? visibility : 'PRIVATE'
    },
    update() {
      if (achievement && visibility) {
        updateAchievementVisibilityInCache(achievement.publicId, visibility);
      }
    }
  });
  const {
    isUpdatingAchievement,
    setIsUpdatingAchievement
  } = useAchievementUpdating();
  const mutateAchievement = useCallback(() => {
    if (achievement) {
      setIsUpdatingAchievement(achievement.publicId, true);
      return mutate().then(data => {
        setIsUpdatingAchievement(achievement.publicId, false);
        return data;
      });
    }
    return null;
  }, [achievement, mutate, setIsUpdatingAchievement]);
  return [mutateAchievement, {
    updateAchievementData,
    updateAchievementLoading,
    updateAchievementGlobalLoading: achievement ? isUpdatingAchievement[achievement.publicId] : false,
    updateAchievementError,
    updateAchievementCalled
  }];
};
export function getMetadataLanguageFallback(metadata, targetLanguage) {
  return metadata[targetLanguage] ? metadata[targetLanguage] : metadata[Object.keys(metadata)[0]];
}
export const useTrackPublicData = trackId => {
  const {
    data,
    loading,
    error
  } = useGetByIdQuery({
    variables: {
      trackId: trackId
    },
    skip: !trackId
  });
  return {
    track: data === null || data === void 0 ? void 0 : data.track,
    trackLoading: loading,
    trackError: error
  };
};